/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to style components
 */
import AuthenticatedUserContext from "@/contexts/AuthenticatedUserContext";
import { faFileChartColumn } from "@fortawesome/pro-duotone-svg-icons";
import { Box, Container, Typography, styled } from "@mui/material";
import {
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo
} from "react";
import colorBlackWhite from "../../assets/img/logo-b-w.png";
import colorLogoNew from "../../assets/img/logo-h-c.png";
import Header, { IMenuItem } from "../Header";
import MuiXLicense from "../MuiXLicense";

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

export interface IPageLayoutProps extends PropsWithChildren {
  breadcrumbs?: ReactNode;
  heading?: string;
  headerOny?: boolean;
  fullWidth?: boolean;
}
/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const TitlePadded = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 0, 2, 0),
  font: "roboto",
  fontWeight: 200,
}));

const StyledBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const BreadcrumbBox = styled(Box)(() => ({
  display: "flex",
}));

const BreadcrumbContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(9, 0, 1, 0),
  backgroundColor: theme.palette.background.default, 
}));

const TitleContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'hasBreadcrumbs' && prop !== 'hasHeading' })<{ hasBreadcrumbs: boolean, hasHeading: boolean }>(({ theme, hasHeading }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  paddingTop: hasHeading ? theme.spacing(8) : theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const Background = styled("div")(({ theme }) => ({

  backgroundColor: theme.palette.background.default,
  height: "100vh",
}));

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */




const PageLayout: React.FunctionComponent<IPageLayoutProps> = ({
  breadcrumbs,
  children,
  heading,
  headerOny = false,
  fullWidth = false,
}) => {
  const menuItems: IMenuItem[] = useMemo(() => {
    const items: IMenuItem[] = [
      {
        url: "/biobank",
        label: "BCT Biobank",
        external: false,
        newTab: false,
        icon: faFileChartColumn,
      },
      
    ];

    return items;
  }, []);

const { data: user } = useContext(AuthenticatedUserContext);

  const contain = true;
  const Component = fullWidth ? Box : contain ? StyledContainer : StyledBox;
  const BreadcrumbComponent = contain ? BreadcrumbContainer : BreadcrumbBox;
  return (
    <Background>
      <Header
        trialName={"Tissue Bank"}
        organisationName={"BCT"}
        organisationLogoLight={colorLogoNew}
        organisationLogoDark={colorBlackWhite}
        organisationLogoMobileLight={undefined}
        organisationLogoMobileDark={undefined}
        applyDarkMode={() => { } }
        user={user}
        menuItems={menuItems} protocolId={undefined}>
        
        
      </Header>
      {!headerOny && (
        <>
          <TitleContainer hasBreadcrumbs={!!breadcrumbs} hasHeading={!!heading}>
            {breadcrumbs && (
              <BreadcrumbComponent>{breadcrumbs}</BreadcrumbComponent>
            )}
          </TitleContainer>
          {heading && (
            <Component>
              <TitlePadded color="secondary" variant="h1">
                {heading}
              </TitlePadded>
            </Component>
          )}
        </>
      )}
      {headerOny ? children : <Component>{children}</Component>}
        <MuiXLicense />
    </Background>
  );
};

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PageLayout;
